/**
 * The four non-content states every staffing page can be in, in one place so the
 * tabs cannot describe the same condition two different ways.
 *
 * "Not configured" is a FIRST-RUN state, not an error: cm-api answers 200 with
 * `config: null` because the module having no saved model yet is normal, and the
 * page has to say what to do about it rather than showing an empty table that
 * looks like a book with no customers in it.
 */
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@andavo/web-ui';
import { SeedStaffingModel } from './SeedStaffingModel';

export interface StaffingScreenStateProps {
  loading: boolean;
  /** Transport / API failure — already unwrapped to cm-api's own message. */
  error: string | null;
  /** The engine refused the inputs. Distinct from a fetch failure. */
  modelError: string | null;
  configured: boolean;
  /**
   * How many what-if changes are applied, if any.
   *
   * Only used to tell a broken SCENARIO from a broken saved model, which are
   * different problems with different owners: one is discarded in a click, the
   * other needs a configuration fixed.
   */
  scenarioChanges?: number;
  /** The way back out of a scenario the engine refused. */
  onResetScenario?: () => void;
}

export function StaffingScreenState({
  loading,
  error,
  modelError,
  configured,
  scenarioChanges,
  onResetScenario,
}: StaffingScreenStateProps) {
  if (loading) {
    return (
      <p className="text-content-secondary text-sm" role="status">
        Loading the staffing book…
      </p>
    );
  }

  if (error) {
    return (
      <Card data-testid="staffing-error">
        <CardHeader>
          <CardTitle>The staffing book could not be loaded</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-content-secondary text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!configured) {
    return (
      <Card data-testid="staffing-unconfigured">
        <CardHeader>
          <CardTitle>No staffing model yet</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p className="text-content-secondary">
            This module reads customers, booking volume and current owners from
            the CRM, and owns only the staffing model on top of them — tiers,
            team slots and pins. None of that has been created yet.
          </p>
          <p className="text-content-secondary">
            Seeding a starting model from the current CRM book creates the
            roster and the tier defaults without changing any customer record.
          </p>
          {/* The card used to describe this action and offer no way to take it,
              so the only route to a working module was a POST fired by hand. */}
          <SeedStaffingModel />
        </CardContent>
      </Card>
    );
  }

  if (modelError) {
    // A SCENARIO can make the engine refuse — model everyone in a tier leaving
    // and the roster it is handed is empty. That is not the saved model being
    // broken, and calling it that sends someone to fix a configuration that is
    // fine. Worse, this card replaces the whole page, including the controls
    // that hold the way back, so without the button below the only exit from an
    // ordinary what-if is a page reload.
    const fromScenario = (scenarioChanges ?? 0) > 0;
    return (
      <Card data-testid="staffing-model-error">
        <CardHeader>
          <CardTitle>
            {fromScenario
              ? 'This scenario cannot be computed'
              : 'The saved model cannot be computed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {/* The engine's own message names the offending value. Paraphrasing it
              would drop the one detail that makes it fixable. */}
          <p className="text-content-secondary">{modelError}</p>
          {fromScenario ? (
            <>
              <p className="text-content-secondary">
                The saved model is unaffected — this is only the what-if.
              </p>
              {onResetScenario ? (
                <div>
                  <Button onClick={onResetScenario} size="sm">
                    Discard scenario
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return null;
}
